/**
 * Centralized sanitization utilities.
 * - sanitizeHtml: allow-listed tag/attribute sanitizer for rich text
 * - sanitizeCsvCell / sanitizeCsvRow: Excel/CSV injection prevention
 */

const ALLOWED_TAGS = [
  "b",
  "i",
  "u",
  "em",
  "strong",
  "s",
  "del",
  "strike",
  "a",
  "p",
  "br",
  "ul",
  "ol",
  "li",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "pre",
  "code",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
  "span",
  "div",
  "img",
  "hr",
  "sub",
  "sup",
];

const ALLOWED_ATTRS = ["href", "src", "alt", "title", "class", "style", "target", "width", "height"];

/**
 * Sanitises an HTML string by keeping only allow-listed tags and
 * attributes. Removes event handlers, javascript: URIs, etc.
 * Safe for rendering in SimpleEditor / RichTextEditor.
 */
export function sanitizeHtml(html: string | undefined | null): string {
  if (!html) return "";

  const doc = new DOMParser().parseFromString(html, "text/html");

  function walk(node: Node): Node {
    if (node.nodeType === Node.TEXT_NODE) return node.cloneNode();

    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;
      const tag = el.tagName.toLowerCase();

      if (!ALLOWED_TAGS.includes(tag)) {
        const fragment = document.createDocumentFragment();
        for (const child of Array.from(el.childNodes)) {
          fragment.appendChild(walk(child));
        }
        return fragment;
      }

      const clean = document.createElement(tag);
      for (const attr of Array.from(el.attributes)) {
        if (ALLOWED_ATTRS.includes(attr.name.toLowerCase())) {
          const value = attr.value.toLowerCase().trim();
          if (attr.name === "href" || attr.name === "src") {
            if (value.startsWith("javascript:") || value.startsWith("data:text/html")) continue;
          }
          clean.setAttribute(attr.name, attr.value);
        }
      }
      for (const child of Array.from(el.childNodes)) {
        clean.appendChild(walk(child));
      }
      return clean;
    }

    return document.createDocumentFragment();
  }

  const fragment = document.createDocumentFragment();
  for (const child of Array.from(doc.body.childNodes)) {
    fragment.appendChild(walk(child));
  }

  const wrapper = document.createElement("div");
  wrapper.appendChild(fragment);
  return wrapper.innerHTML;
}

// ============================================================================
// CSV / Excel Injection Prevention
// ============================================================================

const FORMULA_PREFIXES = ["=", "+", "-", "@", "\t", "\r"];

/**
 * Wraps a single cell value so it cannot be interpreted as a formula
 * by Excel / Google Sheets. Non-string values pass through unchanged.
 */
export function sanitizeCsvCell(value: unknown): unknown {
  if (typeof value !== "string") return value;
  if (value.length === 0) return value;
  if (FORMULA_PREFIXES.some((p) => value.startsWith(p))) {
    return `'${value}`;
  }
  return value;
}

/**
 * Sanitise every element of a row array (convenience wrapper for
 * ExcelJS addRow calls).
 */
export function sanitizeCsvRow(row: unknown[]): unknown[] {
  return row.map(sanitizeCsvCell);
}
